import type { ProductDetailAttribute } from '~/types/enrichment';
import type {
  AttributeDiffRow,
  AttributeDiffSection,
  AttributeDiffSide,
  DiffStatus,
} from '~/types/product-diff';

/**
 * Section order a merchandiser reads a spec sheet in — fabric first, use case
 * last. Deliberately neither payload order (which is generation order) nor
 * alphabetical (which would put Color Details before Fabric). Any section the
 * payload carries that isn't listed here renders after these, in payload order.
 */
export const SECTION_ORDER = [
  'Fabric',
  'Fit',
  'Measurements',
  'Construction',
  'Design',
  'Color Details',
  'Use case',
] as const;

/** Trailing bucket for current attributes that were dropped with no optimized
 *  attribute claiming them. A section is never inferred for these — guessing one
 *  would tell a reviewer the data was reorganised when it was discarded. */
export const NOT_CARRIED_OVER = '__not_carried_over__';

/** One attribute after its repeated payload entries have been folded together. */
interface CollapsedAttribute {
  section: string;
  name: string;
  values: string[];
  /** Name of the current attribute this one supersedes, when the payload says so. */
  replaces?: string;
}

/** Zeroed status tally — every ledger starts from a full key set so a band or
 *  legend can index any status without a null check. */
function emptyCounts(): Record<DiffStatus, number> {
  return { added: 0, edited: 0, replaced: 0, removed: 0, unchanged: 0 };
}

/**
 * Collapses the payload's repeated entries into one row per
 * `(sectionName, attributeName)`, preserving first-seen order for both the keys
 * and the values within a key.
 *
 * Multi-value attributes arrive as N entries sharing a name (`Activity` once per
 * value), so this is what turns four "Activity" entries into one row of four
 * values. Exported for the attribute-count summaries, which count attributes
 * rather than payload entries.
 */
export function collapseAttributes(attrs: ProductDetailAttribute[]): CollapsedAttribute[] {
  const byKey = new Map<string, CollapsedAttribute>();
  for (const attr of attrs) {
    // Tab-separated: a section or attribute name can contain a colon or space,
    // and a collision here would silently merge two different attributes.
    const key = `${attr.sectionName}\t${attr.attributeName}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.values.push(attr.attributeValue);
      // A multi-value attribute repeats across entries; the link only has to be
      // stated on one of them for the whole group to carry it.
      if (existing.replaces === undefined && attr.replaces !== undefined) {
        existing.replaces = attr.replaces;
      }
      continue;
    }
    byKey.set(key, {
      section: attr.sectionName,
      name: attr.attributeName,
      values: [attr.attributeValue],
      ...(attr.replaces === undefined ? {} : { replaces: attr.replaces }),
    });
  }
  return [...byKey.values()];
}

/** Case-, whitespace- and order-insensitive value-set comparison. Without the
 *  normalisation `["Navy"]` vs `[" navy "]` reads as an edit, and the table fills
 *  with phantom changes a reviewer then has to dismiss one by one. */
function sameValues(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const norm = (values: string[]): string[] =>
    values.map((v) => v.trim().replace(/\s+/g, ' ').toLowerCase()).sort();
  const left = norm(a);
  const right = norm(b);
  return left.every((value, i) => value === right[i]);
}

/** `Renamed · 2 values collapsed to 1` — the two facts that show a `replaced`
 *  row is a normalisation and not a deletion. */
function replacementNote(before: AttributeDiffSide, after: AttributeDiffSide): string {
  const parts: string[] = [];
  if (before.name !== after.name) parts.push('Renamed');
  if (before.values.length !== after.values.length) {
    const unit = before.values.length === 1 ? 'value' : 'values';
    parts.push(
      `${String(before.values.length)} ${unit} collapsed to ${String(after.values.length)}`,
    );
  }
  return parts.join(' · ');
}

/** Position of a section in the fixed order. Unknown sections sort after every
 *  known one, and the `Not carried over` bucket sorts last of all. */
function sectionRank(section: string): number {
  if (section === NOT_CARRIED_OVER) return SECTION_ORDER.length + 1;
  const index = (SECTION_ORDER as readonly string[]).indexOf(section);
  return index === -1 ? SECTION_ORDER.length : index;
}

interface BuildOptions {
  /** When false, every `replaced` row splits back into its `removed` + `added`
   *  halves, each noting its counterpart — for reviewers who want to see the
   *  underlying facts rather than the pairing's claim about them. */
  pairReplacements?: boolean;
}

/**
 * Derives the section-grouped attribute diff between the current and optimized
 * attribute lists. Pure and memoisable on the two payload references — the
 * components never diff during render, they filter what this returns.
 *
 * Pairing is driven entirely by the optional `replaces` field on an optimized
 * attribute. No string similarity, no mapping table: a wrong pairing tells a
 * merchandiser data was preserved when it wasn't, which is worse than showing a
 * rename as a removal plus an addition.
 */
export function buildAttributeDiff(
  current: ProductDetailAttribute[],
  enriched: ProductDetailAttribute[],
  { pairReplacements = true }: BuildOptions = {},
): AttributeDiffSection[] {
  const currentRows = collapseAttributes(current);
  const enrichedRows = collapseAttributes(enriched);

  // Current attributes are keyed by name alone: the current side's `sectionName`
  // comes from a different taxonomy than the optimized one (one catch-all bucket
  // in practice), so matching on section as well would never pair anything.
  const currentByName = new Map(currentRows.map((row) => [row.name, row]));
  /** Current names an optimized attribute has claimed, by same-name match or by
   *  an explicit link. Whatever is left over was genuinely dropped. */
  const claimed = new Set<string>();
  const rows: AttributeDiffRow[] = [];

  for (const row of enrichedRows) {
    const id = `${row.section}:${row.name}`;
    const after: AttributeDiffSide = { name: row.name, values: row.values };
    // An explicit link wins over a same-name match: the payload is stating which
    // current attribute this one supersedes.
    const linked = row.replaces === undefined ? undefined : currentByName.get(row.replaces);

    if (linked) {
      claimed.add(linked.name);
      const before: AttributeDiffSide = { name: linked.name, values: linked.values };
      const note = replacementNote(before, after);
      rows.push({
        id,
        section: row.section,
        status: 'replaced',
        before,
        after,
        ...(note === '' ? {} : { note }),
      });
      continue;
    }

    const same = currentByName.get(row.name);
    if (!same) {
      rows.push({ id, section: row.section, status: 'added', after });
      continue;
    }

    claimed.add(same.name);
    rows.push({
      id,
      section: row.section,
      status: sameValues(same.values, row.values) ? 'unchanged' : 'edited',
      before: { name: same.name, values: same.values },
      after,
    });
  }

  // Whatever no optimized attribute claimed was dropped. These carry no section
  // in the optimized taxonomy, so they go to the trailing bucket.
  for (const row of currentRows) {
    if (claimed.has(row.name)) continue;
    rows.push({
      id: `${NOT_CARRIED_OVER}:${row.name}`,
      section: NOT_CARRIED_OVER,
      status: 'removed',
      before: { name: row.name, values: row.values },
    });
  }

  return groupIntoSections(pairReplacements ? rows : rows.flatMap(unpairRow));
}

/**
 * Splits one `replaced` row into the `removed` + `added` pair it was built from,
 * each naming its counterpart so the connection survives the split. Both halves
 * stay in the replacing attribute's section — the link is known here, so the
 * `Not carried over` bucket (which means "no link at all") would be a lie.
 * Rows of every other status pass through untouched.
 */
export function unpairRow(row: AttributeDiffRow): AttributeDiffRow[] {
  if (row.status !== 'replaced' || !row.before || !row.after) return [row];
  const { before, after } = row;
  return [
    {
      id: `${row.section}:removed:${before.name}`,
      section: row.section,
      status: 'removed',
      before,
      note: `Replaced by ${after.name}`,
    },
    {
      id: row.id,
      section: row.section,
      status: 'added',
      after,
      note: `Replaces ${before.name}`,
    },
  ];
}

/** Buckets rows into bands in the fixed section order, tallying each band's
 *  status ledger as it goes. */
function groupIntoSections(rows: AttributeDiffRow[]): AttributeDiffSection[] {
  const bySection = new Map<string, AttributeDiffSection>();
  for (const row of rows) {
    let section = bySection.get(row.section);
    if (!section) {
      section = { section: row.section, rows: [], counts: emptyCounts() };
      bySection.set(row.section, section);
    }
    section.rows.push(row);
    section.counts[row.status] += 1;
  }
  // Array#sort is stable, so sections sharing a rank keep payload order.
  return [...bySection.values()].sort((a, b) => sectionRank(a.section) - sectionRank(b.section));
}

/** Flat row list across every section — counts, legend and search all operate on
 *  rows rather than bands. */
export function flattenSections(sections: AttributeDiffSection[]): AttributeDiffRow[] {
  return sections.flatMap((section) => section.rows);
}

/** Totals across every section, for the legend and the section-title summary. */
export function tallyStatuses(rows: AttributeDiffRow[]): Record<DiffStatus, number> {
  const counts = emptyCounts();
  for (const row of rows) counts[row.status] += 1;
  return counts;
}

/** True when the payload carries at least one replacement link. Gates the
 *  pairing control: a checkbox that cannot change anything is worse than none. */
export function hasReplacementLinks(enriched: ProductDetailAttribute[]): boolean {
  return enriched.some((attr) => attr.replaces !== undefined && attr.replaces !== '');
}

/** Case-insensitive match across the section, both attribute names and both
 *  sides' values — searching "navy" has to find the row whether the colour sits
 *  on the current side, the optimized side, or both. `needle` is expected
 *  pre-trimmed and lowercased by the caller. */
export function rowMatchesSearch(row: AttributeDiffRow, needle: string): boolean {
  if (needle === '') return true;
  return [
    row.section,
    row.before?.name ?? '',
    row.after?.name ?? '',
    ...(row.before?.values ?? []),
    ...(row.after?.values ?? []),
  ]
    .join(' ')
    .toLowerCase()
    .includes(needle);
}
